// PRD.315 — DOB / reported-age reconciliation prompt.
//
// When a birth date is added or changed (or a reported age is edited) and both
// values are present, they are compared and a conflict is surfaced BEFORE the
// write lands. The comparison itself is pure and lives in data-access
// (`checkReportedAgeAgainstDob`); this module is only the interaction: hold the
// save, show the discrepancy, and let a human decide.
//
// The prompt never rewrites anything by itself (PRD §10.3). "Keep Date of
// Birth" proceeds with exactly what the user typed, leaving the reported age
// intact as historical provenance; "Review" cancels the save and returns to the
// still-open form with the entered values preserved.

import { useCallback, useRef, useState } from 'react';
import { Modal } from '@helios/ui/overlay';
import { Button } from '@helios/ui/button';
import { checkReportedAgeAgainstDob, type ReportedAgeCheck } from '@helios/data-access';
import { formatReportedOn } from './patient-age-display';

type Mismatch = Extract<ReportedAgeCheck, { status: 'mismatch' }>;

export interface PatientAgeConflict {
  /**
   * Run the consistency check and, on conflict, block until the user decides.
   * Resolves true when the save may proceed, false when the user wants to go
   * back and review. Callers await this before writing.
   */
  guard: (params: {
    birthDate: string | null;
    ageReported: number | null;
    ageReportedAt: string | null;
  }) => Promise<boolean>;
  /** Spread onto <PatientAgeDobConflictModal />. */
  modalProps: {
    conflict: Mismatch | null;
    onKeepDob: () => void;
    onReview: () => void;
  };
}

export function usePatientAgeDobConflict(): PatientAgeConflict {
  const [conflict, setConflict] = useState<Mismatch | null>(null);
  // The pending save's decision channel. A ref (not state) because the promise
  // must be settled exactly once, by whichever button the user presses.
  const resolverRef = useRef<((proceed: boolean) => void) | null>(null);

  const settle = useCallback((proceed: boolean) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setConflict(null);
    resolve?.(proceed);
  }, []);

  const guard = useCallback<PatientAgeConflict['guard']>((params) => {
    const result = checkReportedAgeAgainstDob({
      birthDate: params.birthDate,
      ageReported: params.ageReported,
      ageReportedAt: params.ageReportedAt,
    });
    // Nothing to reconcile — the overwhelmingly common path, and it must not
    // cost the user a dialog (PRD §10.2).
    if (result.status === 'ok') return Promise.resolve(true);

    // A previous prompt somehow never settled: release it rather than
    // stranding that save forever.
    resolverRef.current?.(false);
    setConflict(result);
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  return {
    guard,
    modalProps: {
      conflict,
      onKeepDob: () => settle(true),
      onReview: () => settle(false),
    },
  };
}

export function PatientAgeDobConflictModal({
  conflict,
  onKeepDob,
  onReview,
  brandTokens,
}: {
  conflict: Mismatch | null;
  onKeepDob: () => void;
  onReview: () => void;
  brandTokens?: Record<string, string>;
}) {
  const reportedOn = conflict ? formatReportedOn(conflict.reportedAt) : '';

  return (
    <Modal
      open={conflict !== null}
      // Dismissing by backdrop/Escape/X means "I did not decide", which is the
      // same outcome as Review: cancel the save, keep the form open.
      onOpenChange={(next) => {
        if (!next) onReview();
      }}
      title="Age and date of birth don't match"
      portalStyle={brandTokens as React.CSSProperties}
      maxWidth={460}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-3, 12px)' }}>
          <Button variant="outline" size="lg" onClick={onReview}>
            Review
          </Button>
          <Button variant="gradient" size="lg" onClick={onKeepDob}>
            Keep Date of Birth
          </Button>
        </div>
      }
    >
      {conflict && (
        <div
          style={{
            padding: 'var(--sp-6, 24px)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-3, 12px)',
            fontSize: 'var(--font-size-base, 14px)',
            lineHeight: 'var(--line-height-body, 20px)',
            color: 'var(--text, #111827)',
          }}
        >
          <div>
            The recorded age was <strong>{conflict.reportedAge}</strong>
            {reportedOn ? ` on ${reportedOn}` : ''}.
          </div>
          <div>
            The entered date of birth corresponds to age <strong>{conflict.dobAgeAtReport}</strong>
            {reportedOn ? ` on that date` : ''}.
          </div>
          <div style={{ color: 'var(--text-muted, #64748b)' }}>
            Keeping the date of birth uses it for the patient's current age. The reported age stays
            on the record as history and is not changed.
          </div>
        </div>
      )}
    </Modal>
  );
}
